<template>
  <!-- SPEC §7.1: versão de produção do ReasonField do mockup (decisões 35, 38, 39, 40, 46). -->
  <div
    class="flex flex-col gap-3 text-left rounded-lg border border-primary/20 bg-primary/5 p-4"
    :data-testid="`change-reason-field-${kind}`"
  >
    <div class="flex items-start gap-2">
      <v-icon :icon="mdiCommentQuestionOutline" color="primary" size="22" />
      <div class="flex flex-col">
        <span class="font-semibold text-textPrimary">{{ title }} *</span>
        <span class="text-sm text-[#68688D]">{{ CHANGE_REASON_HELP_TEXT }}</span>
      </div>
    </div>

    <v-select
      :model-value="modelValue.code"
      :menu-props="{ maxHeight: 560 }"
      :items="options"
      item-title="title"
      item-value="value"
      :label="label"
      placeholder="Informe aqui o motivo dessa alteração"
      persistent-placeholder
      variant="outlined"
      color="primary20"
      bg-color="white"
      density="comfortable"
      :error-messages="codeErrors"
      :hide-details="codeErrors.length === 0"
      :data-testid="`change-reason-select-${kind}`"
      @update:model-value="onCode"
    />

    <v-textarea
      v-if="requiresText"
      :model-value="modelValue.note"
      label="Descreva o motivo *"
      placeholder="Conte em poucas palavras o que aconteceu"
      variant="outlined"
      color="primary20"
      bg-color="white"
      rows="3"
      auto-grow
      :counter="CHANGE_REASON_NOTE_MAX"
      :counter-value="changeReasonNoteLength"
      :maxlength="CHANGE_REASON_NOTE_MAX"
      persistent-hint
      hint="Obrigatório para este motivo"
      :error-messages="noteErrors"
      :data-testid="`change-reason-note-input-${kind}`"
      @update:model-value="onNote"
    />

    <div
      v-if="scopeNote"
      class="flex items-center gap-2 text-sm text-textPrimary"
      :data-testid="`change-reason-scope-note-${kind}`"
    >
      <v-icon :icon="mdiInformationOutline" color="primary" size="18" />
      <span>{{ scopeNote }}</span>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { mdiCommentQuestionOutline, mdiInformationOutline } from '@mdi/js'
import type {
  ChangeReasonCode,
  ChangeReasonKind
} from '@/Network/Types/Requests/ChangeReasonRequests'
import {
  CHANGE_REASON_ERROR_MESSAGES,
  CHANGE_REASON_HELP_TEXT,
  CHANGE_REASON_NOTE_MAX,
  changeReasonNoteLength,
  getChangeReasonError,
  type ChangeReasonOption,
  type ChangeReasonValue
} from '@/utils/changeReasons'

const props = withDefaults(
  defineProps<{
    kind: ChangeReasonKind
    title: string
    label: string
    options: ChangeReasonOption[]
    modelValue: ChangeReasonValue
    showErrors?: boolean
    scopeNote?: string
  }>(),
  { showErrors: false, scopeNote: '' }
)

const emit = defineEmits<{ 'update:modelValue': [value: ChangeReasonValue] }>()

const requiresText = computed(
  () => props.options.find((o) => o.value === props.modelValue.code)?.requiresText ?? false
)
const error = computed(() =>
  props.showErrors ? getChangeReasonError(props.modelValue, props.options) : null
)
const codeErrors = computed(() =>
  error.value === 'CODE_REQUIRED' ? [CHANGE_REASON_ERROR_MESSAGES.CODE_REQUIRED] : []
)
const noteErrors = computed(() =>
  error.value && error.value !== 'CODE_REQUIRED' ? [CHANGE_REASON_ERROR_MESSAGES[error.value]] : []
)

function onCode(code: ChangeReasonCode | null) {
  emit('update:modelValue', { code: code ?? null, note: props.modelValue.note })
}

function onNote(note: string | null) {
  emit('update:modelValue', { code: props.modelValue.code, note: note ?? '' })
}
</script>
